import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, FlaskConical, Search, Info, Mail, Home } from "lucide-react";
import { useAuth, useCart } from "@/App";
import { useLang } from "@/context/LangContext";
import { AnnouncementBar } from "@/components/layout/AnnouncementBar";
import {
  NavLinksDesktop,
  LanguageToggle,
  CartButton,
  UserMenuDesktop,
  MobileNavLinks,
  MobileUserSection,
  LOGO_URL,
} from "@/components/layout/NavbarParts";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const { lang, toggle: toggleLang, t } = useLang();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-close mobile menu when the route changes.
  useEffect(() => { setMobileOpen(false); }, [location]);

  const navLinks = [
    { href: "/", label: t("nav_home"), icon: Home },
    { href: "/shop", label: t("nav_shop"), icon: FlaskConical },
    { href: "/about", label: t("nav_about"), icon: Info },
    { href: "/contact", label: t("nav_contact"), icon: Mail },
    { href: "/verify", label: t("nav_verify"), icon: Search },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#06070A]/95 backdrop-blur-xl border-b border-white/10 shadow-lg"
            : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <AnnouncementBar />
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-12">
          <nav className="flex items-center justify-between h-24 sm:h-28 md:h-32">
            <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="logo-link">
              <img src={LOGO_URL} alt="Hexxium Research" className="h-32 sm:h-36 md:h-44 lg:h-48 w-auto max-w-[56vw] sm:max-w-none object-contain object-left" />
            </Link>

            {/* Desktop nav — centered */}
            <div className="hidden lg:flex items-center justify-center flex-1 px-12">
              <NavLinksDesktop navLinks={navLinks} pathname={location.pathname} />
            </div>

            {/* Right-side actions */}
            <div className="flex items-center gap-1 sm:gap-2 md:gap-4 flex-shrink-0">
              <LanguageToggle lang={lang} onToggle={toggleLang} />
              <CartButton cartCount={cartCount} />
              <UserMenuDesktop user={user} t={t} onLogout={handleLogout} />
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2.5 text-slate-300 hover:text-white hover:bg-white/5 rounded-full transition-all"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </nav>
        </div>
      </motion.header>

      {/* Mobile menu panel */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 right-0 bottom-0 w-[300px] bg-[#0A0B0E] border-l border-white/10 z-50 lg:hidden overflow-y-auto"
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <img src={LOGO_URL} alt="Hexxium" className="h-8" />
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 text-slate-400 hover:text-white rounded-full hover:bg-white/5"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <MobileNavLinks navLinks={navLinks} pathname={location.pathname} />

              <div className="p-6 border-t border-white/10">
                <MobileUserSection user={user} t={t} onLogout={handleLogout} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
